import { Box, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { SearchInput } from 'common/ui';
import { TableV2 } from 'components/Table/TableV2';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useUpdateCalendarMeetingTemplateMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { calendarMeetingTemplateColumns } from '../constants/calendarMeetingTemplateColumns';
import { useEffect, useMemo, useState } from 'react';
import { CalendarMeetingTemplateActions } from './CalendarMeetingTemplateActions';
import { TextBlock } from 'common/ui/TextBlock';
import {
  CalendarMeetingTemplate,
  CalendarMeetingTemplateStatus
} from 'generated-graphql/graphql';
import { useConfirmationModal } from 'services/modals/hooks';
import { ConfirmationModalType } from 'services/modals/constants';
import { getTemplateEditBlockedReason } from 'modules/calendar/utils/getTemplateEditBlockedReason';
import { useLoadingOverlay } from 'services/loading/hooks';
import { ToastType, useToast } from 'services/toast';
import { useListActiveMeetingTemplates } from '../api/useListActiveMeetingTemplates';
import { RouterLink } from 'common/ui/RouterLink';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: 2,
  my: '20px'
};

type Props = {
  showCreateButton: boolean;
};

export const CalendarMeetingTemplateList: React.FC<Props> = ({
  showCreateButton
}) => {
  const [search, setSearch] = useState('');
  const organization = useOrganization();
  const navigate = useNavigate();
  const { showModal } = useConfirmationModal();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { showToast } = useToast();
  const { state, pathname, search: queryString, hash } = useLocation();

  const {
    data: templates,
    updateQuery,
    loading
  } = useListActiveMeetingTemplates();

  const [updateCalendarMeetingTemplate] =
    useUpdateCalendarMeetingTemplateMutation();

  // The table paginates at 50, so unsorted a new template lands on the last page.
  const templatesFiltered = templates
    .filter((template) =>
      template?.title.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => (b?.createdAt ?? '').localeCompare(a?.createdAt ?? ''));

  const removeCalendarMeetingTemplateLocally = (id: string) => {
    updateQuery((prev) => {
      if (!prev?.searchCalendarMeetingTemplates) {
        return prev;
      }

      return {
        ...prev,
        searchCalendarMeetingTemplates: {
          ...prev.searchCalendarMeetingTemplates,
          items:
            prev?.searchCalendarMeetingTemplates?.items?.filter(
              (template) => template?.id !== id
            ) || []
        }
      };
    });
  };

  const updateCalendarMeetingTemplateLocally = (
    id: string,
    data: CalendarMeetingTemplate
  ) => {
    updateQuery((prev) => {
      if (!prev?.searchCalendarMeetingTemplates) {
        return prev;
      }

      const existingTemplates = prev.searchCalendarMeetingTemplates.items || [];

      const existingTemplate = existingTemplates.find(
        (template) => template?.id === id
      );

      return {
        ...prev,
        searchCalendarMeetingTemplates: {
          ...prev.searchCalendarMeetingTemplates,
          items: existingTemplate
            ? existingTemplates.map((template) =>
                template?.id === id ? { ...template, ...data } : template
              )
            : [...existingTemplates, { ...data, id }]
        }
      };
    });
  };

  const columns = useMemo(() => {
    const columns = [...calendarMeetingTemplateColumns];

    const archiveMeetingTemplate = async (id: string) => {
      try {
        showLoadingOverlay();

        await updateCalendarMeetingTemplate({
          variables: {
            input: { id, status: CalendarMeetingTemplateStatus.Archived }
          }
        });

        removeCalendarMeetingTemplateLocally(id);

        showToast({
          title: 'Template archived',
          type: ToastType.Success
        });
      } catch {
        showToast({
          title: 'Something went wrong',
          type: ToastType.Error
        });
      } finally {
        hideLoadingOverlay();
      }
    };

    columns.push({
      isAction: true,
      cell: (row) => (
        <Box sx={{ display: 'flex' }}>
          <CalendarMeetingTemplateActions
            id={row.id}
            editBlockedReason={getTemplateEditBlockedReason(row)}
            onEdit={() => {
              navigate(`${Paths.CALENDAR_MEETING_TEMPLATE_EDIT}/${row.id}`);
            }}
            onArchive={() => {
              showModal({
                title: 'Archive this template?',
                message:
                  'This meeting template will no longer be available for future campaigns',
                modalType: ConfirmationModalType.DESTRUCTIVE,
                destructiveText: 'Archive',
                onDestructiveConfirm: async () => {
                  await archiveMeetingTemplate(row.id);
                }
              });
            }}
          />
        </Box>
      )
    });

    return columns;
  }, [
    hideLoadingOverlay,
    navigate,
    showLoadingOverlay,
    showModal,
    showToast,
    updateQuery
  ]);

  // The search index lags a create, so the new row arrives on the route state.
  // Gate on `loading` rather than a row count, or an organization's first
  // template never lands; then spend the state, or it re-injects on every back
  // navigation and resurrects the row after an archive.
  useEffect(() => {
    const created = state?.calendarMeetingTemplate;

    if (!created || loading) {
      return;
    }

    updateCalendarMeetingTemplateLocally(created.id, created);
    navigate(
      { pathname, search: queryString, hash },
      { replace: true, state: null }
    );
  }, [state?.calendarMeetingTemplate?.id, loading]);

  return (
    <>
      <TextBlock
        variant="section"
        title="Meeting templates"
        subtitle={`Add meeting templates to campaigns for candidate scheduling. Available to all ${organization.name} members.`}
      />
      <Box sx={{ mt: 3 }} />
      <Box sx={container}>
        <SearchInput
          sx={{ width: 320 }}
          placeholder="Search templates"
          onChange={(e) => setSearch(e)}
        />
        {showCreateButton && (
          <RouterLink to={Paths.CALENDAR_MEETING_TEMPLATE_CREATE}>
            <Button variant="primary">Create template</Button>
          </RouterLink>
        )}
      </Box>
      <TableV2
        columns={columns}
        data={templatesFiltered}
        pagination
        emptyStateComponent={
          <Typography variant="body2">
            {search
              ? `No templates match "${search}". Try a different search.`
              : 'No meeting templates yet. Create one to start scheduling interviews.'}
          </Typography>
        }
      />
    </>
  );
};
